import { isBasketPaymentSuccessHash } from './profile/profileTabs';

/** sessionStorage key: set when redirecting to Viva for `orderPackage`; read on `#paySuccess`. */
export const PACKAGE_PAY_PENDING_STORAGE_KEY = 'lb_packagePayPending';

export type VivaPaymentPurpose = 'orderCheckout' | 'orderPackage' | 'topUp';

export type SessionStorageLike = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

/**
 * On full page load: keep package-pending only on `#paySuccess`; clear a stale flag on any other entry.
 * Matches OrderApp `packagePaymentReturnRef` initializer.
 */
export function readPackagePaymentReturnOnLoad(
    locationHash: string,
    storage: SessionStorageLike,
    storageKey: string = PACKAGE_PAY_PENDING_STORAGE_KEY,
): string | null {
    let pending: string | null = null;
    try {
        pending = storage.getItem(storageKey);
    } catch {
        pending = null;
    }
    if (!isBasketPaymentSuccessHash(locationHash)) {
        if (pending) {
            try {
                storage.removeItem(storageKey);
            } catch {
                // ignore
            }
        }
        return null;
    }
    return pending;
}

export function shouldRunBasketFulfillmentOnPaySuccess(args: {
    isPaySuccessReturn: boolean;
    basketPayHandled: boolean;
    /** `useOrderCheckoutSession()` has fired at least once (`!== undefined`). */
    checkoutSessionKnown: boolean;
    packagePaymentPendingId: string | null;
}): boolean {
    const { isPaySuccessReturn, basketPayHandled, checkoutSessionKnown, packagePaymentPendingId } = args;
    if (!isPaySuccessReturn || basketPayHandled || !checkoutSessionKnown) {
        return false;
    }
    if (packagePaymentPendingId) {
        return false;
    }
    return true;
}

export function shouldRunPackageFulfillmentOnPaySuccess(args: {
    isPaySuccessReturn: boolean;
    packagesResolved: boolean;
    packagePayHandled: boolean;
    packagePaymentPendingId: string | null;
}): boolean {
    const { isPaySuccessReturn, packagesResolved, packagePayHandled, packagePaymentPendingId } = args;
    if (!isPaySuccessReturn || !packagesResolved || packagePayHandled) {
        return false;
    }
    if (!packagePaymentPendingId) {
        return false;
    }
    return true;
}

/** Resolve catalog package id after Viva (RTDB temp row may already be cleared server-side). */
export function resolvePlaceOrderPackageId(args: {
    packagePaymentPendingId: string;
    snapshotPackageId?: string | null;
}): string | null {
    const storedId = args.packagePaymentPendingId !== '1' ? args.packagePaymentPendingId : null;
    return args.snapshotPackageId || storedId || null;
}

/** Sync sessionStorage before Viva redirect (sync part of `createOrderPayment`). */
export function syncPackagePayPendingSessionBeforeVivaRedirect(
    purpose: VivaPaymentPurpose,
    storage: SessionStorageLike,
    options?: { packageId?: string },
    storageKey: string = PACKAGE_PAY_PENDING_STORAGE_KEY,
): void {
    if (purpose === 'orderPackage') {
        try {
            storage.setItem(storageKey, options?.packageId || '1');
        } catch {
            // private mode / blocked storage — vivaSuccess/webhook still fulfills orderPackage server-side
        }
    } else if (purpose === 'orderCheckout') {
        try {
            storage.removeItem(storageKey);
        } catch {
            // ignore
        }
    }
}
